import React, { useState } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Avatar,
    Typography,
    Divider,
    Button,
    Modal,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
} from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchPromptById } from '../api/promptApi';
import { getApi } from '../api/axiosInstance';
import { formatDate } from '../utils/dateUtils';

// Remove /api from the base URL for static assets
const STATIC_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

const getImageUrl = (filepath) => {
    if (!filepath) return null;
    // Ensure the filepath starts with a forward slash and remove any duplicate slashes
    const cleanPath = '/' + filepath.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${STATIC_BASE_URL}${cleanPath}`;
};

const PromptCard = ({ id, prompt, created_at, user_id, user_email, metadata, handleDelete }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [details, setDetails] = useState(null);
    const [generatedContent, setGeneratedContent] = useState(null);

    const handleOpen = async () => {
        setOpen(true);
        setLoading(true);
        setError(null);
        try {
            const [promptDetails, contentResponse] = await Promise.all([
                fetchPromptById(id),
                getApi().get(`/generated/prompt/${id}`)
            ]);

            if (promptDetails) {
                setDetails(promptDetails);
            } else {
                setError('No prompt details found');
            }

            if (contentResponse.data && contentResponse.data.length > 0) {
                const content = contentResponse.data[0];
                console.log('Generated content:', content);
                console.log('Image URL:', getImageUrl(content.image_url));
                setGeneratedContent(content);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to fetch details');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setDetails(null);
        setGeneratedContent(null);
    };

    const handleDownloadImage = async () => {
        if (generatedContent?.image_url) {
            try {
                const imageUrl = getImageUrl(generatedContent.image_url);
                console.log('Downloading image from:', imageUrl);
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `generated-image-${id}.png`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } catch (err) {
                console.error('Error downloading image:', err);
            }
        }
    };

    return (
        <>
            <Card
                sx={{
                    maxWidth: 345,
                    margin: 'auto',
                    transition: '0.3s',
                    boxShadow: '0 8px 40px -12px rgba(0,0,0,0.3)',
                    '&:hover': {
                        boxShadow: '0 16px 70px -12.125px rgba(0,0,0,0.3)',
                    },
                }}
            >
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: deepPurple[500] }}>
                            {user_email ? user_email.charAt(0).toUpperCase() : 'U'}
                        </Avatar>
                    }
                    subheader={user_email || 'Unknown User'}
                />
                <Divider variant="middle" />
                <CardContent>
                    <Typography variant="body2" color="textSecondary">
                        {prompt}
                    </Typography>
                    <Divider />
                    <Typography variant="caption" color="textSecondary">
                        Created on: {formatDate(created_at)}
                    </Typography>
                </CardContent>
                <CardActions>
                    <Button size="small" onClick={handleOpen}>
                        Details
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(id)}
                    >
                        Delete
                    </Button>
                </CardActions>
            </Card>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="prompt-detail-title"
                aria-describedby="prompt-detail-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        maxWidth: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 2,
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography id="prompt-detail-title" variant="h6" component="h2">
                            Prompt Details
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                    
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    ) : details ? (
                        <>
                            {generatedContent?.image_url && (
                                <>
                                    <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                                        <img
                                            src={getImageUrl(generatedContent.image_url)}
                                            alt="Generated content"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: '4px',
                                            }}
                                            onError={(e) => {
                                                console.error('Image failed to load:', e);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadImage}
                                            sx={{
                                                position: 'absolute',
                                                bottom: 16,
                                                right: 16,
                                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                },
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                </>
                            )}

                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Prompt
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {details.prompt}
                            </Typography>
                            <Divider sx={{ my: 2 }} />

                            {details.metadata && (
                                <>
                                    {details.metadata.primaryImage && (
                                        <>
                                            <Typography variant="h6">Primary Image</Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Description: {details.metadata.primaryImage.description}
                                            </Typography>
                                        </>
                                    )}

                                    {details.metadata.secondaryImage && (
                                        <>
                                            <Typography variant="h6">Secondary Image</Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Description: {details.metadata.secondaryImage.description}
                                            </Typography>
                                        </>
                                    )}

                                    {details.metadata.classifiers && details.metadata.classifiers.length > 0 && (
                                        <>
                                            <Typography variant="h6" sx={{ mt: 2 }}>Classifiers</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {details.metadata.classifiers.map((classifier) => (
                                                    <Chip 
                                                        key={`classifier-${classifier.id}-${classifier.classText}`} 
                                                        label={classifier.classText} 
                                                    />
                                                ))}
                                            </Box>
                                        </>
                                    )}

                                    {details.metadata.knowledge && details.metadata.knowledge.length > 0 && (
                                        <>
                                            <Typography variant="h6" sx={{ mt: 2 }}>Knowledge</Typography>
                                            {details.metadata.knowledge.map((kn) => (
                                                <Box key={kn.id} sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle2">{kn.title}</Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {kn.content}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    ) : null}
                </Box>
            </Modal>
        </>
    );
};

export default PromptCard;
